import { styled } from '@linaria/react';
import { Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Measure, type FlowByPhaseDataOverTime } from '../../../api/work-periods-client/work-periods-client.type.ts';
import { useGetWorkflows } from '../../../api/workflows-client/workflows.client.hooks';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader.tsx';
import { formatDate } from '../../../helpers/date-time-helpers/formatting/formatting';
import { safeParseDateInput } from '../../../helpers/date-time-helpers/parsing/parsing.ts';
import { resolvePhaseLabels } from '../../../helpers/phase-labels/phase-labels';
import type { PhaseLabel } from '../../../helpers/phase-labels/phase-labels.type';
import { useSubprojectStatuses } from '../../../api/subprojects-client/subprojects-client.hooks';
import { buildStatusLabelMap, createStatusLabelResolver } from '../../../helpers/status-labels/status-labels';
import {
  createSubGroupColorMap,
  extractPhaseKeys,
  findWorkflowForSubproject,
  getOrderedStatusesFromWorkflow,
  sortPhaseKeysByWorkflow,
} from '../../../helpers/subgroup-helpers/subgroup-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store.ts';
import { newCOLORS } from '../../../styles/colors';
import { skyLight } from '../../../styles/design-tokens';
import { getPhasesMetadata } from '../../strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.constants';
import type { Phase } from '../../strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.type';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import { useFlowByPhaseData, usePayload, useWorkPeriodsTasksData } from '../hooks/work-periods-calculation.hooks.ts';
import type { WorkPeriod } from '../work-periods.type';
import { Metric } from '../work-periods.type';
import { calculateCategoryPercentages, filterPhaseData, transformForPhaseChart } from './phase-section.helpers.ts';
import { WorkPhaseDistributionChartLegend } from './work-phase-distribution-chart-legend';
import { WorkPhaseDistributionChartPhases } from './work-phase-distribution-chart-phases';

type WorkPhaseDistributionChartProps = {
  selectedWorkPeriod: WorkPeriod | null;
  selectedDate: string | null;
  visiblePhases: Record<string, boolean>;
  tasksOpen: boolean;
  onTasksChange: (tasks: TaskRecord[], title?: string, isLoading?: boolean) => void;
  onPhaseToggle: (phase: Phase) => void;
  useGroups: boolean;
  flowData?: FlowByPhaseDataOverTime;
  /**
   * Optional `phases` companion array sourced from the same `flow_by_phase`
   * response that produced `flowData`. Drives per-workflow display labels
   * for the phase tiles, tooltip phase name, and bottom legend.
   */
  phases?: PhaseLabel[];
};

/**
 * Stacked area chart of work distribution across phases for a work period, in
 * either grouped (Bloomfilter phase) or sub-group (source status) mode.
 *
 * Responsibilities:
 * 1. Fetch flow-by-phase data for the selected work period and metric.
 * 2. In sub-group mode, order keys by the board's workflow, assign colors, and
 *    resolve display labels — keys stay raw so they keep seeding the color map
 *    and the selected-sub-group set, while labels carry the board's source casing.
 * 3. Drive the task panel when a phase or legend entry is clicked.
 *
 * @param {WorkPeriod} selectedWorkPeriod - Work period whose distribution is charted
 * @param {string | null} selectedDate - Date the tiles and tooltip describe
 * @param {string[]} visiblePhases - Phases currently toggled on in grouped mode
 * @param {PhaseLabel[]} [phases] - Optional per-workflow label overrides for phase tiles and legend
 * @returns {JSX.Element} The area chart with phase tiles, tooltip, and legend
 */
export function WorkPhaseDistributionChart({
  selectedWorkPeriod,
  selectedDate,
  visiblePhases,
  tasksOpen,
  onTasksChange,
  onPhaseToggle,
  useGroups,
  flowData: propFlowData,
  phases: propPhases,
}: WorkPhaseDistributionChartProps) {
  const i18n = useTranslation();
  const { selectedMetric, selectedBoardId } = useWorkPeriodStore();
  const organization = useGlobalStore((state) => state.organization);
  const [selectedPhase, setSelectedPhase] = useState<Phase | null>(null);

  // Data fetching - moved up so we can use it for filtering phasesMetadata
  const { data: hookFlowData, phases: hookPhases, isLoading } = useFlowByPhaseData();
  const flowData = propFlowData || hookFlowData;
  // Pair `phases` with whichever source provided `flowData`. When the parent
  // injects `propFlowData` without `propPhases`, falling back to `hookPhases`
  // would mismatch — the hook's scope (board/work-period) can resolve to a
  // different workflow, so labels would describe the wrong dataset. In that
  // case prefer `undefined` and let `resolvePhaseLabels` use canonical i18n
  // defaults.
  const phases = propPhases ?? (propFlowData ? undefined : hookPhases);
  const resolvedLabels = useMemo(() => resolvePhaseLabels(phases, i18n.t), [phases, i18n.t]);
  const allPhasesMetadata = useMemo(() => getPhasesMetadata(i18n.t, resolvedLabels), [i18n.t, resolvedLabels]);

  // Extract available phases from the API response for groups mode
  // This filters out phases that have no status mappings configured
  const availablePhasesFromApi = useMemo(() => {
    if (!useGroups || !flowData) return null;
    const keys = extractPhaseKeys(flowData);
    // Filter to only include valid Phase enum values
    const validPhaseIds = allPhasesMetadata.map((p) => p.id);
    return keys.filter((key) => validPhaseIds.includes(key as Phase));
  }, [useGroups, flowData, allPhasesMetadata]);

  // Filter phase metadata to only include phases returned by the API
  // When availablePhasesFromApi is null (no flow data yet), show all phases
  const phasesMetadata = useMemo(() => {
    if (!availablePhasesFromApi) return allPhasesMetadata;
    return allPhasesMetadata.filter((phase) => availablePhasesFromApi.includes(phase.id));
  }, [allPhasesMetadata, availablePhasesFromApi]);

  const [selectedSubGroups, setSelectedSubGroups] = useState<Set<string>>(new Set());
  const { ref: containerRef, width: containerWidth } = useElementSize();

  // Fetch workflows for ordering subgroups
  const { data: workflows } = useGetWorkflows(organization?.id || null);

  // Task loading state for phase clicks
  const [taskQuery, setTaskQuery] = useState<{
    filter: string;
    workPeriodId: string;
  } | null>(null);

  // Ref to track if we should be calling onTasksChange
  const shouldCallOnTasksChange = useRef(false);

  // Get base payload for tasks
  const basePayload = usePayload([Measure.FlowByPhase]);

  const { data: tasksQueryData = [], isFetching: isTasksFetching } = useWorkPeriodsTasksData(
    [Measure.FlowByPhase],
    taskQuery?.filter,
    { enabled: !!taskQuery },
    taskQuery
      ? {
          ...basePayload,
          measure_filter: taskQuery.filter,
          use_groups: useGroups,
        }
      : undefined,
  );

  // Sub-group keys are raw status names parsed out of the `tasks_*`/`points_*`
  // response keys, so they carry whatever casing the API stored. Labels come
  // from the board's status list; keys stay untouched because they also seed
  // the color map and the selected-sub-group set.
  const { statuses: boardStatuses } = useSubprojectStatuses(selectedBoardId);
  const resolveStatusLabelForDisplay = useMemo(
    () => createStatusLabelResolver(buildStatusLabelMap(boardStatuses)),
    [boardStatuses],
  );

  // Find workflow for current subproject and get ordered statuses
  const workflowStatusOrder = useMemo(() => {
    if (useGroups || !workflows || !selectedBoardId) return [];
    const workflow = findWorkflowForSubproject(workflows, selectedBoardId);
    return getOrderedStatusesFromWorkflow(workflow);
  }, [useGroups, workflows, selectedBoardId]);

  // Create color map for subgroups - use workflow ordering for consistent colors
  const subGroupColorMap = useMemo(() => {
    if (useGroups) return null;
    const phaseKeys = extractPhaseKeys(flowData);
    // Sort by workflow order before creating color map to ensure consistent colors
    const sortedKeys = sortPhaseKeysByWorkflow(phaseKeys, workflowStatusOrder);
    return createSubGroupColorMap(sortedKeys);
  }, [useGroups, flowData, workflowStatusOrder]);

  // Dynamic phase metadata for sub-groups (when useGroups is false)
  // Uses workflow ordering for correct process flow sequence
  const subGroupPhaseMetadata = useMemo(() => {
    if (useGroups) return [];
    const phaseKeys = extractPhaseKeys(flowData);
    // Sort phase keys according to workflow step/mapping order
    const sortedKeys = sortPhaseKeysByWorkflow(phaseKeys, workflowStatusOrder);
    const metadata = sortedKeys.map((key) => ({
      id: key,
      label: resolveStatusLabelForDisplay(key),
      description: key,
      color: subGroupColorMap?.[key] || newCOLORS.indigo,
    }));
    return metadata;
  }, [useGroups, flowData, subGroupColorMap, workflowStatusOrder, resolveStatusLabelForDisplay]);

  // Preserve original order of subgroups
  const subGroupOrder = useMemo(() => {
    if (useGroups || !subGroupColorMap || subGroupPhaseMetadata.length === 0) {
      return null;
    }
    return subGroupPhaseMetadata.map((item) => item.id);
  }, [useGroups, subGroupPhaseMetadata, subGroupColorMap]);

  // Keyed on the sub-group ids rather than the metadata objects: labels resolve
  // asynchronously as the board's statuses arrive, and re-running the reset on
  // a label change would wipe the user's legend selection mid-interaction.
  // JSON rather than a delimiter join: ids are raw source status names, which
  // may legitimately contain any character including a separator.
  const subGroupIdKey = useMemo(
    () => JSON.stringify(subGroupPhaseMetadata.map((item) => item.id)),
    [subGroupPhaseMetadata],
  );

  // Initialize all sub-groups as selected when switching to sub-groups view
  useEffect(() => {
    if (useGroups) {
      return;
    }
    const subGroupIds = JSON.parse(subGroupIdKey) as string[];
    if (subGroupIds.length > 0) {
      setSelectedSubGroups(new Set(subGroupIds));
    }
  }, [useGroups, subGroupIdKey]);

  // Use appropriate metadata based on toggle state
  const allPhaseMetadata = useGroups ? phasesMetadata : subGroupPhaseMetadata;

  // Helper function to get phase label from metadata
  const getPhaseLabel = useCallback(
    (phaseId: string): string => {
      const phase = allPhaseMetadata.find((p) => (typeof p === 'string' ? p : p.id) === phaseId);
      if (phase) {
        return typeof phase === 'string' ? phase : phase.label;
      }
      // Fallback for a phase with no metadata entry: try the board's status
      // labels, then the raw key. Never invent casing — a raw lowercase label
      // here means the status is genuinely absent from the board's list.
      return resolveStatusLabelForDisplay(phaseId);
    },
    [allPhaseMetadata, resolveStatusLabelForDisplay],
  );

  // Effect to clear taskQuery when tasks panel is closed
  useLayoutEffect(() => {
    if (!tasksOpen) {
      shouldCallOnTasksChange.current = false;
      setTaskQuery(null);
      setSelectedPhase(null);
    }
  }, [tasksOpen]);

  // Effect to call onTasksChange when task data changes
  useEffect(() => {
    if (!taskQuery || !onTasksChange || !shouldCallOnTasksChange.current) return;

    const parts = taskQuery.filter.split(',');

    if (parts.length !== 2) {
      console.warn('Unexpected filter format:', taskQuery.filter);
      return;
    }

    const [phaseId, date] = parts;
    const title = `${getPhaseLabel(phaseId)} - ${formatDate(date, { format: 'MMM D, YYYY' })}`;

    onTasksChange(tasksQueryData as TaskRecord[], title, isTasksFetching);
  }, [tasksQueryData, isTasksFetching, taskQuery, onTasksChange, getPhaseLabel]);

  // Chart data processing
  const filteredData = useMemo(() => {
    const result = filterPhaseData(flowData, selectedMetric);
    return result;
  }, [flowData, selectedMetric]);

  // Base chart data - always contains ALL subgroups (unfiltered)
  // When useGroups is false, normalize data to ensure all unique keys exist in each point
  const baseChartData = useMemo(() => {
    const rawData = transformForPhaseChart(filteredData);
    if (!useGroups && rawData.length > 0) {
      // Collect all unique keys (excluding 'date')
      const allKeys = new Set<string>();
      rawData.forEach((point) => {
        Object.keys(point).forEach((key) => {
          if (key !== 'date') {
            allKeys.add(key);
          }
        });
      });

      // Normalize each data point to include all keys with 0 as default
      // and coerce any nullish values to 0
      return rawData.map((point) => {
        const normalizedPoint: { date: string; [key: string]: string | number } = { date: point.date };
        allKeys.forEach((key) => {
          const value = point[key as keyof typeof point];
          normalizedPoint[key] = value ?? 0;
        });
        return normalizedPoint;
      });
    }

    return rawData;
  }, [filteredData, useGroups]);

  // Filtered chart data for rendering - only includes selected subgroups
  const chartData = useMemo(() => {
    // If in subgroup mode, filter to only selected subgroups
    if (!useGroups && baseChartData.length > 0) {
      return baseChartData.map((point) => {
        const filteredPoint: any = { date: point.date };
        selectedSubGroups.forEach((key) => {
          if (key in point) {
            filteredPoint[key] = point[key as keyof typeof point];
          }
        });
        return filteredPoint;
      });
    }
    return baseChartData;
  }, [baseChartData, useGroups, selectedSubGroups]);

  // Filter chart data to only show data up to the selected date (or today if selected date is in the future)
  const filteredChartData = useMemo(() => {
    if (!selectedDate || !chartData.length) return chartData;

    const selectedDateObj = safeParseDateInput(selectedDate);
    if (!selectedDateObj) return chartData;

    // If selected date is in the future, cap at today
    const today = dayjs().endOf('day');
    const cutoffDate = selectedDateObj.valueOf() > today.valueOf() ? today : selectedDateObj;

    const filtered = chartData.map((dataPoint) => {
      const dataPointDate = safeParseDateInput(dataPoint.date);
      if (!dataPointDate) return dataPoint;

      // If the data point is after the cutoff date, set its value to null but keep the date
      if (dataPointDate.valueOf() > cutoffDate.valueOf()) {
        // Set all phase values to null, but keep the date
        const { date, ...rest } = dataPoint;
        const nullified = Object.fromEntries(Object.entries(rest).map(([key]) => [key, null]));
        return { date, ...nullified };
      }
      // Otherwise, keep the data point as is
      return dataPoint;
    });
    return filtered;
  }, [chartData, selectedDate]);

  // Calculate phase percentages - always calculate based on ALL subgroups
  // Selection only affects chart visibility, not percentage values
  const phasePercentages = useMemo(() => {
    if (!selectedDate) return null;
    // Always calculate percentages based on ALL subgroups (baseChartData, not filtered chartData)
    const percentages = calculateCategoryPercentages(baseChartData, selectedDate);
    if (!percentages) return null;

    // Remove the 'date' field to match the expected type Record<string, number>
    const { date: _date, ...numericPercentages } = percentages;
    return numericPercentages as Record<string, number>;
  }, [selectedDate, baseChartData]);

  // Phase click handler
  const handlePhaseClick = (forDate: string, phase: Phase, selectedWorkPeriodId?: string) => {
    shouldCallOnTasksChange.current = true;
    setSelectedPhase(phase);
    setTaskQuery({
      filter: `${phase},${forDate}`,
      workPeriodId: selectedWorkPeriodId || '',
    });
  };

  const handleSubGroupToggle = (id: string, checked: boolean) => {
    setSelectedSubGroups((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  // Sanitize phase ID for use in SVG gradient IDs (remove special characters)
  const sanitizeId = (id: string): string => {
    return id.replace(/[^a-zA-Z0-9-_]/g, '-');
  };

  return (
    <ChartWrapper>
      <BrandedLoadingOverlay variant="colored" visible={isLoading} scoped />
      <WorkPhaseDistributionChartPhases
        containerRef={containerRef}
        containerWidth={containerWidth}
        allPhaseMetadata={allPhaseMetadata.map((phase) => {
          const phaseId = typeof phase === 'string' ? phase : phase.id;
          const phaseLabel = typeof phase === 'string' ? phase : phase.label;
          const phaseDescription = typeof phase === 'string' ? phase : phase.description;
          const phaseColor = typeof phase === 'string' ? newCOLORS.indigo : phase.color;
          return {
            id: phaseId,
            label: phaseLabel,
            description: phaseDescription,
            color: phaseColor,
          };
        })}
        phasePercentages={phasePercentages}
        selectedDate={selectedDate}
        selectedWorkPeriod={selectedWorkPeriod}
        selectedPhase={selectedPhase}
        tasksOpen={tasksOpen}
        onPhaseClick={handlePhaseClick}
      />

      {filteredChartData.length > 0 ? (
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={filteredChartData} margin={{ top: 20, right: 30, left: 10, bottom: 0 }}>
              <defs>
                {(() => {
                  // Sort subgroups by original order (create gradients for ALL, not just visible)
                  const sorted =
                    useGroups || !subGroupOrder
                      ? allPhaseMetadata
                      : allPhaseMetadata.slice().sort((a, b) => {
                          const aId = typeof a === 'string' ? a : a.id;
                          const bId = typeof b === 'string' ? b : b.id;
                          return subGroupOrder.indexOf(aId) - subGroupOrder.indexOf(bId);
                        });

                  // Create gradients for ALL phases so they're always available
                  return sorted.map((phase) => {
                    const phaseId = typeof phase === 'string' ? phase : phase.id;
                    const phaseColor = typeof phase === 'string' ? newCOLORS.indigo : phase.color;
                    const sanitizedId = sanitizeId(phaseId);
                    return (
                      <linearGradient
                        key={`gradient-${sanitizedId}`}
                        id={`color-${sanitizedId}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="20%" stopColor={phaseColor} stopOpacity={0.6} />
                        <stop offset="80%" stopColor={phaseColor} stopOpacity={0.3} />
                      </linearGradient>
                    );
                  });
                })()}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => dayjs(value).format('MMM D')}
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: skyLight }}
                tickLine={{ stroke: skyLight }}
                padding={{ right: 10 }}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: skyLight }}
                tickLine={{ stroke: skyLight }}
                label={{
                  value: selectedMetric === Metric.Tasks ? i18n.t('common.work_units') : i18n.t('common.estimation'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' },
                  offset: 10,
                }}
              />
              <Tooltip
                itemSorter={(item) => {
                  const itemId = item.dataKey as string;
                  if (useGroups) {
                    const index = phasesMetadata.findIndex((p) => p.id === itemId);
                    return index === -1 ? 999 : index;
                  } else if (subGroupOrder) {
                    const index = subGroupOrder.indexOf(itemId);
                    return index === -1 ? 999 : index;
                  }
                  return 0;
                }}
                formatter={(value, name) => {
                  if (useGroups) {
                    const phase = phasesMetadata.find((p) => p.id === name);
                    const phaseLabel = phase?.label || name || '';
                    return [value ?? '', phaseLabel];
                  } else {
                    const phase = subGroupPhaseMetadata.find((p) => p.id === name);
                    const phaseLabel = phase?.label || resolveStatusLabelForDisplay(String(name ?? ''));
                    return [value ?? '', phaseLabel];
                  }
                }}
                labelFormatter={(label) => `Date: ${formatDate(String(label), { format: 'MMM D, YYYY' })}`}
              />
              <Legend
                content={
                  <WorkPhaseDistributionChartLegend
                    phases={useGroups ? phasesMetadata : subGroupPhaseMetadata}
                    visiblePhases={
                      useGroups
                        ? visiblePhases
                        : Object.fromEntries(subGroupPhaseMetadata.map((p) => [p.id, selectedSubGroups.has(p.id)]))
                    }
                    onPhaseToggle={
                      useGroups
                        ? (phaseId) => onPhaseToggle(phaseId as Phase)
                        : (phaseId) => handleSubGroupToggle(phaseId, !selectedSubGroups.has(phaseId))
                    }
                  />
                }
                verticalAlign="bottom"
                height={36}
              />
              {(() => {
                // Sort subgroups by original order, then reverse for chart stacking
                // Recharts AreaChart renders first element at bottom, last at top
                const sorted =
                  useGroups || !subGroupOrder
                    ? allPhaseMetadata.slice()
                    : allPhaseMetadata.slice().sort((a, b) => {
                        const aId = typeof a === 'string' ? a : a.id;
                        const bId = typeof b === 'string' ? b : b.id;
                        return subGroupOrder.indexOf(aId) - subGroupOrder.indexOf(bId);
                      });

                // Reverse for correct stacking order in Recharts
                const reversed = sorted.reverse();

                // Render ALL areas, but hide unselected ones by setting hide prop
                return reversed.map((phase) => {
                  const phaseId = typeof phase === 'string' ? phase : phase.id;
                  const phaseLabel = typeof phase === 'string' ? phase : phase.label;
                  const phaseColor = typeof phase === 'string' ? newCOLORS.indigo : phase.color;
                  const sanitizedId = sanitizeId(phaseId);

                  // Check if this phase should be visible
                  const isVisible = useGroups ? visiblePhases[phaseId] : selectedSubGroups.has(phaseId);

                  return (
                    <Area
                      key={phaseId}
                      type="monotone"
                      dataKey={phaseId}
                      stackId="1"
                      stroke={phaseColor}
                      fill={`url(#color-${sanitizedId})`}
                      fillOpacity={0.6}
                      strokeOpacity={1}
                      strokeWidth={1}
                      name={phaseLabel}
                      connectNulls={false}
                      animationDuration={0}
                      hide={!isVisible}
                    />
                  );
                });
              })()}
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      ) : (
        <EmptyChartMessage>
          <Text>No flow data available for the selected work period.</Text>
          <Text size="sm" color="dimmed" mt="xs">
            This could be due to insufficient data in the selected time period.
          </Text>
        </EmptyChartMessage>
      )}
    </ChartWrapper>
  );
}

const ChartWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const EmptyChartMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 300px;
  background-color: ${newCOLORS.lightGray};
  border-radius: 4px;
  padding: 16px;
  text-align: center;
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  width: 100%;
`;
